/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { BehaviorSubject } from "rxjs";

import type { Behavior } from "../state/Behavior";
import type {
  CallPresentationAdapter,
  CallPresentationState,
} from "./CallRecoveryAdapters";
import type { TerminalCallError } from "./CallRecoveryTypes";

const INITIAL_STATE: CallPresentationState = {
  cycleId: null,
  recoveryActive: false,
  frozen: false,
  phase: "idle",
  screenShareStoppedCycleId: null,
};

/**
 * Единственная session-scoped граница между технической recovery epoch и UI.
 *
 * Store не запускает transport операции: coordinator сообщает ему только
 * атомарные переходы freeze/unfreeze и завершение lifecycle.
 */
export class DefaultCallPresentationStore implements CallPresentationAdapter {
  private readonly stateSubject$ = new BehaviorSubject<CallPresentationState>(
    INITIAL_STATE,
  );
  public readonly state$: Behavior<CallPresentationState> = this.stateSubject$;
  private readonly recoveryActiveSubject$ = new BehaviorSubject(false);
  /** Freeze-сигнал для presentation consumers без доступа к записи. */
  public readonly recoveryActive$: Behavior<boolean> =
    this.recoveryActiveSubject$;

  public constructor(
    private readonly onTerminal: (error: TerminalCallError) => void,
  ) {}

  public freeze(cycleId: string): void {
    const current = this.stateSubject$.value;
    if (current.frozen && current.cycleId === cycleId) return;

    this.setState({
      cycleId,
      recoveryActive: true,
      frozen: true,
      phase: "recovering",
      screenShareStoppedCycleId:
        current.cycleId === cycleId ? current.screenShareStoppedCycleId : null,
    });
  }

  public unfreeze(cycleId: string): void {
    const current = this.stateSubject$.value;
    if (!current.frozen || current.cycleId !== cycleId) return;

    this.setState({
      ...current,
      recoveryActive: false,
      frozen: false,
      phase: "connected",
    });
  }

  public markScreenShareStopped(cycleId: string): void {
    const current = this.stateSubject$.value;
    if (!current.frozen || current.cycleId !== cycleId) return;
    if (current.screenShareStoppedCycleId === cycleId) return;
    this.setState({
      ...current,
      screenShareStoppedCycleId: cycleId,
    });
  }

  public cancel(cycleId: string | null): void {
    const current = this.stateSubject$.value;
    if (cycleId !== null && current.cycleId !== cycleId) return;
    if (current === INITIAL_STATE) return;
    this.setState({ ...INITIAL_STATE, phase: "cancelled" });
  }

  public showTerminal(error: TerminalCallError): void {
    const current = this.stateSubject$.value;
    // Terminal сохраняет frozen snapshot до того, как этап 8 заменит canvas.
    this.setState({
      ...current,
      recoveryActive: true,
      phase: "terminal",
      screenShareStoppedCycleId: null,
    });
    this.onTerminal(error);
  }

  private setState(state: CallPresentationState): void {
    if (this.recoveryActiveSubject$.value !== state.recoveryActive) {
      this.recoveryActiveSubject$.next(state.recoveryActive);
    }
    // Freeze boundary меняется раньше UI lifecycle: при freeze ни один SDK
    // emission не проскочит, при unfreeze финальный tuple уже применён к VM.
    this.stateSubject$.next(state);
  }
}
